import React, { useLayoutEffect, useEffect, useCallback, useState } from "react";
import { Layout, ConfigProvider, theme } from "antd";
import { useLocation, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Header } from "./Component/header";
import { Login } from "./Pages/Login";
import { RouterWrapper } from "./router/routerWrapper";
import { PermissionSideMenu } from "./Component/PermissionSideMenu";
import ReminderModal from "./Component/ReminderModal/ReminderModal";
import { CompanySetupRequiredModal } from "./Component/CompanySetupRequiredModal";
import { SocketContextProvider } from "./Socket";
import { NotificationsListener } from "./Socket/NotificationsListener";
import { ImportEventsListener } from "./Socket/ImportEventsListener";
import { NotificationsProvider } from "./Socket/notificationsStore";
import { ConnectionAlert } from "./Component/ConnectionAlert/ConnectionAlert";
import { PermissionLoader } from "./Component/PermissionLoader";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorFallback } from "./Component/ErrorFallback/ErrorFallback";
import { useTheme } from "./Hooks/useTheme";
import {
  themeTokens,
  brandTokens,
  assertTokensInSync,
} from "./styles/tokens";
import useTokenRefresh from "./Hooks/useTokenRefresh";
import Api from "./Network";
import { setUserInfo, setCompany } from "./redux/action";
import { message } from "antd";
import { ResponsiveProvider } from "./Hooks/useContext";
import './i18n';
import "./styles/index.scss";
import "./styles/_reset.scss";
import { VoipDialerListener } from './Socket/VoipDialerListener';
import { VoipWidget } from './Component/VOIP/VoipWidget';
import { primeAudioUnlock } from './Utilis/playNotificationSound';
function App() {
  const { Content } = Layout;
  const token = localStorage.getItem("token");
  const history = useHistory();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state: any) => state.setReducer);
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);
  
  // Proactively refresh token before it expires
  useTokenRefresh();

  useEffect(() => {
    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    primeAudioUnlock();
  }, []);
  

  
  const handleLoadUserData = useCallback(async () => {
    setIsLoadingUserData(true);
    try {
      const userResponse = await Api.post("auth/me",{});
      dispatch(setUserInfo(userResponse));

      const companyResponse = await Api.get("company");
      dispatch(setCompany(companyResponse));
    } catch (error: any) {
      // Check if this is a network error (no response object)
      const isNetworkError = !error?.response && (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error');
      
      // Validation errors are already handled by the Network interceptor
      const responseData = error?.response?.data;
      const hasValidationErrors = responseData?.errors || responseData?.status === "error";
      const status = error?.response?.status;
      
      // Check production mode
      const nodeEnv = import.meta.env.MODE;
      const reactAppEnv = import.meta.env.VITE_ENV;
      const isProduction = nodeEnv === 'production' || 
                         reactAppEnv === 'production' || 
                         (nodeEnv !== 'development' && nodeEnv !== 'test' && nodeEnv !== undefined);
      
      // Don't show errors for:
      // 1. Network errors (CORS, connection issues) - these are usually backend/server issues
      // 2. Validation errors (already shown by Network interceptor)
      // 3. Production mode
      // Only show generic errors in development/staging for non-network, non-validation errors
      if (!isNetworkError && !hasValidationErrors && !isProduction) {
        // Only show for actual API errors with response (not network errors)
        if (error?.response) {
          const errorMsg = responseData?.message || "failed to load user data";
          // Show status code and error message in development/staging
          message.error(`${errorMsg} (${status})`);
        }
      }
      // Silently fail for network errors, validation errors, and in production
    } finally {
      setIsLoadingUserData(false);
    }
  }, []);

  useLayoutEffect(() => {
    // If there's no token but userInfo exists, clear the userInfo
    if (!token && userInfo) {
      dispatch(setUserInfo(null));
      return;
    }
    
    // If there's a token and no userInfo, load user data
    if (token && !userInfo) {
    handleLoadUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]); // Removed userInfo from dependencies

  const ScrollToTop = () => {
    useEffect(() => {
      const unListen = history.listen(() => {
        window.scrollTo(0, 0);
      });

      return () => {
        unListen();
      };
    }, [history]);

    return null;
  };
  const { theme: currentTheme, isDark } = useTheme();
  const palette = themeTokens[currentTheme];

  // Dev-only: warn if styles/tokens.ts has drifted from styles/index.scss.
  useEffect(() => {
    assertTokensInSync(currentTheme);
  }, [currentTheme]);
  return (
    <ResponsiveProvider>
      <div className={`App ${isDark ? "dark-theme" : "light-theme"}`}>
        {token ? (
          <SocketContextProvider>
            <ConfigProvider
              theme={{
                // Array form: compact applies in BOTH themes. Previously dark
                // mode swapped compactAlgorithm out for darkAlgorithm, so
                // switching theme also silently changed component density.
                algorithm: isDark
                  ? [theme.compactAlgorithm, theme.darkAlgorithm]
                  : [theme.compactAlgorithm],
                token: {
                  colorPrimary: brandTokens.brand,
                  fontFamily: brandTokens.fontFamily,
                  borderRadius: brandTokens.radiusCard,
                  colorBgLayout: palette["--bg-app"],
                  colorBgContainer: palette["--bg-surface"],
                  colorBgElevated: palette["--bg-surface-raised"],
                  colorText: palette["--text-primary"],
                  colorTextSecondary: palette["--text-secondary"],
                  colorBorder: palette["--border-default"],
                  colorBorderSecondary: palette["--border-subtle"],
                  ...(isDark ? { colorError: "#ff7875" } : {}),
                },
                components: {
                  // Global modal breathing room: the compact algorithm shrinks
                  // antd's default modal padding, leaving content stuck to the edges.
                  // `contentPadding` is applied at runtime but is not part of antd's
                  // public Modal token typings, hence the cast.
                  Modal: {
                    contentPadding: "24px 28px",
                  } as any,
                },
              }}
            >
              <NotificationsProvider>
                <NotificationsListener />
                <ImportEventsListener />
                <VoipDialerListener />
                <VoipWidget />
                <Header />
                <Layout>
                  <PermissionSideMenu />
                  <ReminderModal />
                  <CompanySetupRequiredModal />
                  <Layout className="site-layout">
                    <Content
                      style={{ padding: "15px 20px" }}
                      className="site-layout-background"
                    >
                      <PermissionLoader>
                        <ScrollToTop />
                        <RouterWrapper />
                        <ConnectionAlert />
                      </PermissionLoader>
                    </Content>
                  </Layout>
                </Layout>
              </NotificationsProvider>
            </ConfigProvider>
          </SocketContextProvider>
        ) : (
          <Login />
        )}
      </div>
    </ResponsiveProvider>
  );
}

export default App;
